import { useEffect, useState } from 'react';
import { DocumentArrowDownIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from 'auth/AuthContext';
import { parseDate, calculateNetHours } from 'utils';
import { showAlert } from 'services/alerts.service';
import { makeRequest } from 'services/request.service';
import EditWorkdayModal from 'components/modals/EditWorkdayModal';
import ButtonComponent from 'components/common/ButtonComponent';
import ActionCardComponent from 'components/common/ActionCardComponent';
import SectionIntro from 'components/common/SectionIntro';
import TableComponent from 'components/common/TableComponent';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

const Workdays = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [state, setState] = useState({
    workdays: [],
    selectedWorkDay: null,
    showEditModal: false,
    loading: true,
    filterDate: DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd'),
    searchTerm: ''
  });

  const handleExportExcel = () => {
    try {
      const dataToExport = state.workdays.map(worker => ({
        Number: worker.oldId || worker.workerId,
        [t('workdays.workerName')]: worker.workerName,
        [t('workdays.date')]: worker.date,
        [t('workdays.startTime')]: worker.startTime,
        [t('workdays.endTime')]: worker.endTime,
        [t('workdays.netHours')]: worker.netHours,
        [t('workdays.edited')]: worker.edited ? t('common.yes') : t('common.no')
      }));

      const worksheet = XLSXUtils.json_to_sheet(dataToExport);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Workday');

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workday_${new Date().toISOString().split('T')[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      showAlert({
        text: t('export.excelSuccess'),
        icon: 'success'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showAlert({
        text: t('export.excelFailed'),
        icon: 'error'
      });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF();

      doc.setFontSize(16);
      doc.text(t('workdays.workdayReport'), 14, 15);
      doc.setFontSize(10);
      doc.text(`${t('export.generatedOn')} ${new Date().toLocaleDateString()}`, 14, 25);

      const tableColumn = [
        t('workdays.workerNumber'),
        t('workdays.workerName'),
        t('workdays.date'),
        t('workdays.startTime'),
        t('workdays.endTime'),
        t('workdays.netHours'),
        t('workdays.edited')
      ];

      const tableRows = state.workdays.map(workDay => [
        workDay.workerId,
        workDay.workerName,
        workDay.date,
        workDay.startTime,
        workDay.endTime,
        workDay.netHours,
        workDay.edited ? t('common.yes') : t('common.no')
      ]);

      // @ts-ignore
      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      });

      doc.save(`workday_${new Date().toISOString().split('T')[0]}.pdf`);

      showAlert({
        text: t('export.pdfSuccess'),
        icon: 'success'
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      showAlert({
        text: t('export.pdfFailed'),
        icon: 'error'
      });
    }
  };

  const handleEditWorkday = async workday => {
    try {
      const response = await makeRequest({
        method: 'patch',
        url: `/management/workdays/${workday.workdayId}`,
        data: {
          endTime: workday.endTime,
          startTime: workday.startTime
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      });
      if (response.status === 200) {
        setState(s => ({
          ...s,
          showEditModal: false,
          selectedWorkDay: null
        }));
        await fetchWorkdays();
      }
    } catch (error) {
      showAlert({
        text: t('workdays.failedToUpdate'),
        icon: 'error'
      });
      console.error('Error updating workday:', error);
    }
  };

  const handleDelete = async workday => {
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/workdays/${workday.workdayId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      });
    } catch (error) {
      showAlert({
        text: t('workdays.failedToDelete'),
        icon: 'error'
      });
      console.error('Error deleting workday:', error);
    } finally {
      fetchWorkdays();
    }
  };

  const handleEditModal = workday => {
    setState(prev => ({
      ...prev,
      selectedWorkDay: workday,
      showEditModal: true
    }));
  };

  const handleCloseModal = () => {
    setState(s => ({
      ...s,
      showEditModal: false,
      selectedWorkDay: null
    }));
  };

  const getUrl = () => {
    const baseUrl = `/management/workdays/${user.farmId}`;
    const params = {
      filterDate: new Date(state.filterDate).toISOString()
    };

    const query = Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== null)
      .map(([k, v]) => `${k}=${v}`)
      .join('&');

    return `${baseUrl}?${query}`;
  };

  const fetchWorkdays = async () => {
    try {
      // @ts-ignore
      const response = await makeRequest({
        url: getUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });
      if (response.status === 200) {
        const workdaysWithNetHours = response.data.map(workday => ({
          ...workday,
          date: parseDate(workday.createdAt, false),
          netHours: calculateNetHours(workday.startTime, workday.endTime)
        }));
        setState(s => ({ ...s, workdays: workdaysWithNetHours, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching workdays:', error);
      setState(s => ({ ...s, loading: false }));
    }
  };

  const filteredWorkdays = state.workdays.filter(
    data =>
      data.workerName.toLowerCase().includes(state.searchTerm) ||
      data.date.toLowerCase().includes(state.searchTerm) ||
      data.startTime.toLowerCase().includes(state.searchTerm) ||
      data.netHours.toLowerCase().includes(state.searchTerm)
  );

  const workdayTableData = {
    columns: [
      { key: 'workerId', label: t('workdays.workerNumber') },
      { key: 'workerName', label: t('workdays.workerName') },
      { key: 'date', label: t('workdays.date') },
      { key: 'startTime', label: t('workdays.startTime') },
      { key: 'endTime', label: t('workdays.endTime') },
      { key: 'netHours', label: t('workdays.netHours') },
      {
        key: 'edited',
        label: t('workdays.edited'),
        cell: value => (value ? t('common.yes') : t('common.no'))
      },
      {
        key: 'actions',
        label: t('common.manage'),
        // @ts-ignore
        cell: (value, row) => {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEditModal(row)}
                title={t('common.edit')}
                className="p-1 rounded transition"
              >
                <PencilSquareIcon className="w-5 h-5 text-blue-500" />
              </button>
              <button
                onClick={() => handleDelete(row)}
                title={t('common.delete')}
                className="p-1 rounded transition"
              >
                <TrashIcon className="w-5 h-5 text-red-500" />
              </button>
            </div>
          );
        }
      }
    ],
    rows: filteredWorkdays
  };

  useEffect(() => {
    fetchWorkdays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filterDate]);

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('nav.workday')}
        description={t('pageIntro.workdaysDescription')}
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('workdays.searchWorkday')}
          value={state.searchTerm}
          name="filter"
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />
        <input
          type="date"
          value={state.filterDate}
          onChange={e => setState(s => ({ ...s, filterDate: e.target.value }))}
          className="search-input"
          data-align="left"
        />

        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.excel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.pdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={workdayTableData}
        handleEdit={handleEditModal}
        defaultSortColumn={workdayTableData.columns[0]?.key}
      />

      <EditWorkdayModal
        isOpen={state.showEditModal}
        workday={state.selectedWorkDay}
        onClose={handleCloseModal}
        onSave={handleEditWorkday}
      />
    </div>
  );
};

export default Workdays;
